import { createContext, useCallback, useContext, useState } from 'react';

import { Brand } from '@/models/brand';
import { useLocalStorageState } from '@/services/localStorage';
import useDebouncedState from '@/utils/useDebouncedState';

import { ClientFilterId } from './clientFilterOptions';

type FiltersState = {
  page: {
    page: number;
    setPage: (page: number) => void;
  };
  search: {
    search: string;
    debouncedSearch: string;
    debouncedSetSearch: (search: string) => void;
  };
  agreementTypes: {
    agreementTypes: ('price_catalog' | 'transit' | 'after_sales')[];
    setAgreementTypes: (types: ('price_catalog' | 'transit' | 'after_sales')[]) => void;
  };
  articleBrands: {
    articleBrands: Brand[];
    setArticleBrands: (brands: Brand[]) => void;
  };
  entityOrGroupSelections: {
    entityOrGroupSelections: { id: string; name: string }[];
    setEntityOrGroupSelections: (selections: { id: string; name: string }[]) => void;
  };
  articleWorkshopSelections: {
    articleWorkshopSelections: { id: string; name: string }[];
    setArticleWorkshopSelections: (selections: { id: string; name: string }[]) => void;
  };
  hasClient: {
    hasClient: ClientFilterId[];
    setHasClient: (value: ClientFilterId[]) => void;
  };
  activeFiltersCount: number;
  clearFilters: () => void;
};

export const FiltersStateContext = createContext<FiltersState>({} as FiltersState);

export const useFiltersState = (): FiltersState => {
  const [page, setPage] = useState(1);
  const resetPage = useCallback(() => setPage(1), []);

  const [search, debouncedSearch, debouncedSetSearch] = useDebouncedState('', 300, resetPage);

  const [agreementTypes, setAgreementTypesState] = useLocalStorageState<
    ('price_catalog' | 'transit' | 'after_sales')[]
  >('settings:organization:entities:agreements:filters:types', []);

  const [articleBrands, setArticleBrandsState] = useLocalStorageState<Brand[]>(
    'settings:organization:entities:agreements:filters:articleBrands',
    []
  );

  const [entityOrGroupSelections, setEntityOrGroupSelectionsState] = useLocalStorageState<
    { id: string; name: string }[]
  >('settings:organization:entities:agreements:filters:entityOrGroup', []);

  const [articleWorkshopSelections, setArticleWorkshopSelectionsState] = useLocalStorageState<
    { id: string; name: string }[]
  >('settings:organization:entities:agreements:filters:articleWorkshops', []);

  const [hasClient, setHasClientState] = useLocalStorageState<ClientFilterId[]>(
    'settings:organization:entities:agreements:filters:hasClientSelection',
    []
  );

  return {
    page: {
      page,
      setPage,
    },
    search: {
      search,
      debouncedSearch,
      debouncedSetSearch,
    },
    agreementTypes: {
      agreementTypes,
      setAgreementTypes: (types: ('price_catalog' | 'transit' | 'after_sales')[]) => {
        setAgreementTypesState(types);
        resetPage();
      },
    },
    articleBrands: {
      articleBrands,
      setArticleBrands: (brands: Brand[]) => {
        setArticleBrandsState(brands);
        resetPage();
      },
    },
    entityOrGroupSelections: {
      entityOrGroupSelections,
      setEntityOrGroupSelections: (selections: { id: string; name: string }[]) => {
        setEntityOrGroupSelectionsState(selections);
        resetPage();
      },
    },
    articleWorkshopSelections: {
      articleWorkshopSelections,
      setArticleWorkshopSelections: (selections: { id: string; name: string }[]) => {
        setArticleWorkshopSelectionsState(selections);
        resetPage();
      },
    },
    hasClient: {
      hasClient,
      setHasClient: (value: ClientFilterId[]) => {
        setHasClientState(value);
        resetPage();
      },
    },
    activeFiltersCount:
      agreementTypes.length +
      articleBrands.length +
      entityOrGroupSelections.length +
      articleWorkshopSelections.length +
      hasClient.length,
    clearFilters: () => {
      setAgreementTypesState([]);
      setArticleBrandsState([]);
      setEntityOrGroupSelectionsState([]);
      setArticleWorkshopSelectionsState([]);
      setHasClientState([]);
      resetPage();
    },
  };
};

export const useFilters = () => {
  return useContext(FiltersStateContext);
};
